import { API_URL } from '@/config/constants'
import axios from 'axios'
import { useUserStore } from '@/store/userStore'

const BASE_URL = API_URL

// Don't use proxy in development when using httpOnly cookies
// Cookies set for api.jeridschool.com won't work with localhost:5173/api proxy
// Always use direct API URL to ensure cookies work correctly
const useProxy = false

export const api = axios.create({
  baseURL: useProxy ? '/api' : BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Increase timeout for slow connections
  timeout: 30000,

  // Important for CORS - send cookies automatically (HTTP-only cookies)
  withCredentials: true,
})

// Request interceptor
// HTTP-only cookies are sent automatically with withCredentials: true
// No need to manually attach tokens
api.interceptors.request.use(
  (config) => {
    // Cookies are sent automatically by browser
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't auto-logout for profile update endpoints - let the form handle the error
      const profileUpdateEndpoints = [
        '/user-controller/updateprofile',
        '/user-controller/updateme'
      ]
      
      // Don't auto-logout for login/auth endpoints - let them handle errors
      const authEndpoints = [
        '/auth/login',
        '/auth/register',
        '/auth/logout',
        '/auth/forgot-password',
        '/auth/reset-password'
      ]
      
      // Don't auto-logout for session verification endpoints - they're used to check session status
      // A 401 here just means no valid session, not that we need to logout
      const sessionVerificationEndpoints = [
        '/user-controller/session'
      ]
      
      const isProfileUpdate = profileUpdateEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )
      
      const isAuthEndpoint = authEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )
      
      const isSessionVerification = sessionVerificationEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )

      if (isProfileUpdate) {
    // console.log401 error on profile update - not auto-logging out, letting form handle error')
        // Just return the error, don't logout
        return Promise.reject(error)
      }
      
      if (isAuthEndpoint) {
    // console.log401 error on auth endpoint - not auto-logging out, letting auth handle error')
        // Just return the error, don't logout
        return Promise.reject(error)
      }
      
      if (isSessionVerification) {
    // console.log401 error on session verification endpoint - not auto-logging out, session check failed')
        // Just return the error, don't logout - this is used to check session status
        return Promise.reject(error)
      }
      
      // Check if user exists in store before logging out
      // If user exists, they just logged in - don't logout (cookie might not be ready yet)
      // This respects HTTP-only cookies - if user just logged in, preserve their session
      const user = useUserStore.getState().user
      if (user) {
    // console.log401 error but user exists in store (just logged in) - not logging out, cookie may not be ready yet')
        // Just return the error, don't logout
        return Promise.reject(error)
      }
      
      // Session expired (HTTP-only cookie invalid/expired) - logout and redirect
      // Only logout if no user exists in store (session actually expired)
  // console.logSession expired - logging out and redirecting to login')
      useUserStore.getState().logout().finally(() => {
        // Always redirect to login after logout
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.replace('/login')
        }
      })
    }
    return Promise.reject(error)
  }
)

export default api
